import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, Mail, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import RichText from "@/components/RichText";
import { useContactModal } from "@/components/ContactModalProvider";
import ContactQuoteStepper from "./ContactQuoteStepper";

// Fenêtre du formulaire de contact, ouverte par la section "Contact
// (fenêtre)" (ContactModalSection.jsx). Même endpoint /contact-request en
// sortie que le formulaire pleine page de ContactSection.jsx — seule la
// présentation change.
//
// Responsive : feuille collée en bas de l'écran sur mobile (pouce, coins
// arrondis en haut, hauteur limitée puis défilement interne, marge basse
// respectant l'encoche iOS), fenêtre centrée à partir de `sm`. Un seul
// balisage pour les deux, piloté par les classes `items-end sm:items-center`
// du conteneur — jamais deux composants à maintenir en parallèle.
//
// Rendue dans un portail sur <body> plutôt qu'en place : la page d'accueil
// enveloppe ses sections dans un conteneur `overflow-hidden` animé, et une
// section peut à l'avenir vivre dans un parent transformé — deux cas où un
// `position: fixed` posé DANS la section ne couvrirait plus tout l'écran.

const EMPTY = { name: "", email: "", phone: "", message: "" };

// Éléments capables de recevoir le focus au clavier — sert au piège à focus
// (Tab ne doit jamais sortir de la fenêtre tant qu'elle est ouverte).
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ContactFormModal({
  open,
  onClose,
  heading,
  subheading,
  submitLabel,
  messagePlaceholder,
  confirmationMessage,
  showPhone = true,
  formStyle = "simple",
  quoteOptions = "",
  // Réglages des coordonnées du formulaire en étapes (field_name /
  // field_email / field_phone de la section).
  fieldSettings,
  source = "contact_modal",
}) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const panelRef = useRef(null);
  const firstFieldRef = useRef(null);
  // Élément qui avait le focus à l'ouverture (le bouton du bandeau) — le
  // focus doit y revenir à la fermeture, sinon un visiteur au clavier
  // repart du tout début de la page.
  const openerRef = useRef(null);
  const { notifySent } = useContactModal();

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Réinitialise à chaque ouverture : une demande déjà envoyée ne doit pas
  // rouvrir sur son écran de confirmation.
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setSent(false);
    setSending(false);
  }, [open]);

  // Défilement de la page bloqué tant que la fenêtre est ouverte (sinon le
  // fond défile sous la feuille au doigt), valeur d'origine restaurée telle
  // quelle à la fermeture.
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Le focus part sur le premier champ (ou la fenêtre elle-même pour la
    // variante "étapes", dont le premier élément est un bouton de choix).
    const t = window.setTimeout(() => {
      (firstFieldRef.current || panelRef.current)?.focus?.();
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = previous;
      openerRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = Array.from(panelRef.current?.querySelectorAll(FOCUSABLE) || []).filter(
        (n) => n.offsetParent !== null || n === document.activeElement,
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, close]);

  if (!open || typeof document === "undefined") return null;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-request", {
        name: form.name,
        email: form.email,
        phone: showPhone ? form.phone : "",
        message: form.message,
        source,
      });
      // Pas de toast de succès ici, contrairement au formulaire pleine page
      // (ContactSection.jsx) : la fenêtre affiche déjà sa confirmation, avec
      // le texte choisi par la boutique — un toast codé en dur par-dessus
      // dirait potentiellement autre chose.
      // On ferme cette fenêtre et on laisse la confirmation PARTAGÉE prendre
      // le relais (même message que partout ailleurs sur le site), en lui
      // passant le texte personnalisé de la boutique s'il y en a un. `sent`
      // reste posé pour que la fenêtre ne se rouvre pas sur le formulaire
      // rempli si le visiteur la rappelle aussitôt.
      setSent(true);
      close();
      notifySent(confirmationMessage, { name: form.name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi, réessayez.");
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors";

  // Le titre peut avoir été vidé depuis l'éditeur — RichText ne rend alors
  // rien, et `aria-labelledby` pointerait vers un id absent : la fenêtre se
  // rabat sur un nom en dur plutôt que de rester sans nom accessible.

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-4"
      data-testid="contact-modal-section-modal"
    >
      <div
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm animate-in fade-in"
        onClick={close}
        data-testid="contact-modal-section-overlay"
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={heading ? "contact-modal-section-title" : undefined}
        aria-label={heading ? undefined : "Formulaire de contact"}
        tabIndex={-1}
        className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[88vh] overflow-y-auto bg-cream border-control border-stone rounded-t-2xl sm:rounded-2xl shadow-2xl outline-none animate-in fade-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200 pb-[env(safe-area-inset-bottom)]"
      >
        {/* Poignée décorative — repère de feuille glissante propre au mobile. */}
        <div className="sm:hidden flex justify-center pt-3" aria-hidden="true">
          <span className="h-1 w-10 rounded-full bg-stone" />
        </div>

        <div className="flex items-start justify-between gap-4 p-5 sm:p-6 pb-4 border-b border-stone">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-10 w-10 shrink-0 rounded-xl bg-gold/15 flex items-center justify-center">
              <Mail className="h-5 w-5 text-gold" />
            </span>
            <div className="min-w-0">
              <RichText
                as="h2"
                id="contact-modal-section-title"
                className="font-display font-semibold text-[19px] text-ink leading-tight"
                html={heading}
              />
              {subheading && <RichText as="p" className="text-[13px] text-graphite mt-0.5" html={subheading} />}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Fermer"
            data-testid="contact-modal-section-close"
            className="shrink-0 text-ash hover:text-ink transition-colors p-1 -mr-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-3" data-testid="contact-modal-section-success">
            <span className="h-12 w-12 rounded-full bg-gold/15 flex items-center justify-center">
              <Check className="h-6 w-6 text-gold" />
            </span>
            <h3 className="font-display font-semibold text-[19px] text-ink">Demande envoyée</h3>
            <p className="text-[14px] text-graphite max-w-sm whitespace-pre-line">{confirmationMessage}</p>
            <button
              type="button"
              onClick={close}
              className="mt-2 px-6 py-3 rounded-btn border-control border-stone text-[14px] font-semibold text-ink hover:bg-sand transition-colors"
            >
              Fermer
            </button>
          </div>
        ) : formStyle === "stepper" ? (
          // Formulaire en étapes réutilisé TEL QUEL (il gère ses étapes, son
          // envoi et son écran de confirmation) — la fenêtre ne fait que
          // l'accueillir, exactement comme la section "contact" l'accueille
          // dans sa colonne de droite.
          <div className="p-4 sm:p-5">
            <ContactQuoteStepper quoteOptions={quoteOptions} onSent={close} fieldSettings={fieldSettings} />
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 sm:p-6 space-y-3" data-testid="contact-modal-section-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                ref={firstFieldRef}
                required
                value={form.name}
                onChange={setField("name")}
                placeholder="Nom complet"
                data-testid="contact-modal-section-name"
                className={inputClass}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={setField("email")}
                placeholder="Email"
                data-testid="contact-modal-section-email"
                className={inputClass}
              />
            </div>
            {showPhone && (
              <input
                type="tel"
                value={form.phone}
                onChange={setField("phone")}
                placeholder="Téléphone (optionnel)"
                data-testid="contact-modal-section-phone"
                className={inputClass}
              />
            )}
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={setField("message")}
              placeholder={messagePlaceholder}
              data-testid="contact-modal-section-message"
              className={`${inputClass} resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-modal-section-submit"
              className="w-full flex items-center justify-center gap-2 bg-ink text-white py-3.5 rounded-btn text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Envoi en cours…" : submitLabel}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
